import React from "react";
import {
  resolveColumnInsertionLineRect,
  resolveColumnInsertionPlacement,
} from "../utils/sortableColumnHeaderDrag.js";

const DRAG_THRESHOLD_PX = 5;

function findTargetHeaderCell(clientX, clientY, columnKeyAttribute) {
  const elements = document.elementsFromPoint(clientX, clientY);
  return elements.find((el) => el.tagName === "TH" && el.getAttribute(columnKeyAttribute)) ?? null;
}

function readHeaderKey(headerCell, columnKeyAttribute) {
  return headerCell?.getAttribute(columnKeyAttribute) ?? null;
}

export default function useSortableColumnHeaderDrag({
  columnKey,
  columnKeyAttribute,
  draggable = false,
  label = "",
  onMove = null,
  thRef,
}) {
  const onMoveRef = React.useRef(onMove);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOverlay, setDragOverlay] = React.useState(null);

  React.useEffect(() => {
    onMoveRef.current = onMove;
  }, [onMove]);

  React.useEffect(() => {
    if (!draggable) {
      return undefined;
    }

    const th = thRef.current;
    if (!th) {
      return undefined;
    }

    let dragState = null;

    const removeDocumentListeners = (handlePointerMove, handlePointerUp) => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp, true);
      document.removeEventListener("pointercancel", handlePointerUp, true);
    };

    const clearDragOverlay = () => {
      setDragOverlay(null);
      setIsDragging(false);
      document.body.style.cursor = "";
    };

    const handlePointerDown = (event) => {
      if (event.button !== 0) {
        return;
      }
      if (event.target.closest("button[aria-label*='Redimensionner']")) {
        return;
      }

      const handleDocumentPointerMove = (moveEvent) => {
        if (!dragState) {
          return;
        }

        const dx = moveEvent.clientX - dragState.startX;
        const dy = moveEvent.clientY - dragState.startY;

        if (!dragState.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
          const sourceRect = th.getBoundingClientRect();
          dragState.moved = true;
          dragState.sourceRect = sourceRect;
          dragState.offsetX = moveEvent.clientX - sourceRect.left;
          dragState.offsetY = moveEvent.clientY - sourceRect.top;
          setIsDragging(true);
          document.body.style.cursor = "grabbing";
        }

        if (!dragState.moved || !dragState.sourceRect) {
          return;
        }

        const targetTh = findTargetHeaderCell(moveEvent.clientX, moveEvent.clientY, columnKeyAttribute);
        const targetKey = readHeaderKey(targetTh, columnKeyAttribute);
        let insertionLine = null;

        if (targetTh && targetKey && targetKey !== dragState.columnKey) {
          const placement = resolveColumnInsertionPlacement(
            targetTh.getBoundingClientRect(),
            moveEvent.clientX,
          );
          dragState.lastDropTarget = {
            placement,
            targetKey,
          };
          insertionLine = resolveColumnInsertionLineRect(
            dragState.sourceRect,
            targetTh.getBoundingClientRect(),
            moveEvent.clientX,
          );
        }

        setDragOverlay({
          height: dragState.sourceRect.height,
          insertionLine,
          label,
          offsetX: dragState.offsetX,
          offsetY: dragState.offsetY,
          pointerX: moveEvent.clientX,
          pointerY: moveEvent.clientY,
          width: dragState.sourceRect.width,
        });
      };

      const handleDocumentPointerUp = (upEvent) => {
        if (!dragState) {
          return;
        }

        const wasMoved = dragState.moved;
        const sourceKey = dragState.columnKey;
        const lastDropTarget = dragState.lastDropTarget ?? null;
        const targetTh = findTargetHeaderCell(upEvent.clientX, upEvent.clientY, columnKeyAttribute);
        const targetKey = readHeaderKey(targetTh, columnKeyAttribute);
        const placement = targetTh && targetKey && targetKey !== sourceKey
          ? resolveColumnInsertionPlacement(targetTh.getBoundingClientRect(), upEvent.clientX)
          : null;
        const resolvedTargetKey = targetKey && targetKey !== sourceKey
          ? targetKey
          : lastDropTarget?.targetKey ?? null;
        const resolvedPlacement = targetKey && targetKey !== sourceKey
          ? placement
          : lastDropTarget?.placement ?? null;

        removeDocumentListeners(handleDocumentPointerMove, handleDocumentPointerUp);
        dragState = null;
        clearDragOverlay();

        if (!wasMoved || !resolvedTargetKey || resolvedTargetKey === sourceKey) {
          return;
        }

        if (wasMoved) {
          const suppressClick = (clickEvent) => {
            clickEvent.stopPropagation();
            clickEvent.preventDefault();
            document.removeEventListener("click", suppressClick, true);
          };
          document.addEventListener("click", suppressClick, true);
        }

        onMoveRef.current?.(sourceKey, resolvedTargetKey, resolvedPlacement);
      };

      dragState = {
        columnKey,
        handleDocumentPointerMove,
        handleDocumentPointerUp,
        moved: false,
        startX: event.clientX,
        startY: event.clientY,
      };

      document.addEventListener("pointermove", handleDocumentPointerMove);
      document.addEventListener("pointerup", handleDocumentPointerUp, true);
      document.addEventListener("pointercancel", handleDocumentPointerUp, true);
    };

    th.addEventListener("pointerdown", handlePointerDown);

    return () => {
      th.removeEventListener("pointerdown", handlePointerDown);
      if (dragState?.handleDocumentPointerMove && dragState?.handleDocumentPointerUp) {
        removeDocumentListeners(dragState.handleDocumentPointerMove, dragState.handleDocumentPointerUp);
      }
      dragState = null;
      clearDragOverlay();
    };
  }, [columnKey, columnKeyAttribute, draggable, label, thRef]);

  return {
    dragOverlay,
    isDragging,
  };
}
